/** Edit plátno: DndContext (přesun uzlů + drop z palety) kolem stromu + EditorPanel.
 *  Drop logika: over = `cont:<id>` (drop dovnitř kontejneru, append) | id uzlu (jako sourozenec na jeho index).
 *  moveNode dělá remove-then-insert → move(active, overParent, overIndex) = arrayMove sémantika. Mobil = bez DnD. */

import React, { useState } from "react";
import {
  closestCenter,
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import { findNode, getWidgetType, type LayoutDoc, type LayoutSize } from "@globals/layouts";
import type { WidgetContext } from "@client/config/layoutWidgets";
import { useIsMobile } from "@client/hooks/useIsMobile";
import { useLayoutEditor } from "@client/store/layoutEditor";
import { LayoutNodeView } from "../LayoutNodeView";
import { EditorPanel } from "./EditorPanel";
import { EditToolbar } from "./EditToolbar";

type Props = { draftDoc: LayoutDoc; context?: WidgetContext; scope: "dashboard" | "device"; device?: "pip" | "tracer" | "bms" };
type NewData = { kind: "new"; widgetType: string; cfg?: Record<string, unknown>; size?: LayoutSize; label?: string };

export const EditCanvas: React.FC<Props> = ({ draftDoc, context, scope, device }) => {
  const isMobile = useIsMobile();
  const select = useLayoutEditor((s) => s.select);
  const move = useLayoutEditor((s) => s.move);
  const addWidget = useLayoutEditor((s) => s.addWidget);
  const [label, setLabel] = useState<string | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const tree = (dnd: boolean) => (
    <div>
      <EditToolbar />
      <div className="flex gap-2">
        <div className="min-w-0 flex-1" onClick={() => select(null)}>
          <LayoutNodeView node={draftDoc.root} ctx={context ?? {}} edit dnd={dnd} />
        </div>
        <EditorPanel scope={scope} device={device} slug={context?.slug} />
      </div>
    </div>
  );

  const onDragStart = (e: DragStartEvent) => {
    const d = e.active.data.current as NewData | undefined;
    if (d?.kind === "new") {
      setLabel(d.label ?? "widget");
      return;
    }
    const root = useLayoutEditor.getState().doc?.root;
    const f = root ? findNode(root, String(e.active.id)) : null;
    setLabel(f ? (f.node.kind === "widget" ? getWidgetType(f.node.type)?.label ?? f.node.type : f.node.kind) : "uzel");
  };

  const onDragEnd = (e: DragEndEvent) => {
    setLabel(null);
    const { active, over } = e;
    if (!over) return;
    const root = useLayoutEditor.getState().doc?.root;
    if (!root) return;

    const overId = String(over.id);
    let destParentId: string;
    let destIndex: number;
    if (overId.startsWith("cont:")) {
      destParentId = overId.slice(5);
      const pn = findNode(root, destParentId)?.node;
      destIndex = pn && (pn.kind === "container" || pn.kind === "grid") ? pn.children.length : 0;
    } else {
      const o = findNode(root, overId);
      if (!o || !o.parent || (o.parent.kind !== "container" && o.parent.kind !== "grid")) return; // jen do kontejnerů/mřížek
      destParentId = o.parent.id;
      destIndex = o.index;
    }

    const data = active.data.current as NewData | undefined;
    if (data?.kind === "new" && data.widgetType) {
      addWidget(destParentId, data.widgetType, data.cfg ?? {}, data.size);
    } else {
      const activeId = String(active.id);
      if (activeId !== destParentId) move(activeId, destParentId, destIndex);
    }
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={onDragStart} onDragEnd={onDragEnd} onDragCancel={() => setLabel(null)}>
      {tree(!isMobile)}
      <DragOverlay>{label ? <div className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground shadow-lg">{label}</div> : null}</DragOverlay>
    </DndContext>
  );
};

export default EditCanvas;
